import { SumsubService } from "@/core/services/sumsubService";
import { useManagementMandateStore } from "@/store/management-mandate";
import { useModalStore } from "@/store/modal/store";
import { XIcon } from "@phosphor-icons/react";
import { get } from "lodash";
import Button, { EButtonTypes } from "../../buttons";
import { useTranslation } from "react-i18next";
import SumsubWebSdk from '@sumsub/websdk-react';
import { useState } from "react";
import Loading from "@/core/components/globals/loading";
import { toast } from 'react-toastify';

const InvestorProfile = ({ reset, onClose }: { reset: () => void, onClose: () => void }) => {
  const { t, i18n } = useTranslation();
  const userManagementProfile = useManagementMandateStore((state) => state.userManagementProfile);
  const setModal = useModalStore(state => state.setModal);
  const [showWebSdk, setShowWebSdk] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);

  const accessTokenExpirationHandler = async () => {
    try {
      const response = await SumsubService.getMandateToken();
      setAccessToken(response.token);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching mandate token:', error);
      setLoading(false);
    }
  };

  const messageHandler = (message: unknown) => {
    if (message === 'idCheck.onStepCompleted') {
      onClose();
      toast.success(t('common.success'));
    }
  };

  const errorHandler = (error: unknown) => {
    console.error('Error from Sumsub WebSDK:', error);
  };

  const config = {
    lang: (i18n?.language || "en")
  };

  const options = {};

  const handleConfirm = () => {
    if (userManagementProfile.cefiMandate || userManagementProfile.defiMandate) {
      setLoading(true);
      setShowWebSdk(true);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      accessTokenExpirationHandler().then(() => {});
    } else {
      setModal({ managementProfileSelect: true });
    }
  }

  if (showWebSdk) {
    return (
      <div className="general-modal" style={{ width: "100%", maxWidth: 750, maxHeight: "100%" }}>
        <div className="general-modal__header">
          {t('management_mandate.sign_mandate')}
          <div className="general-modal__close" onClick={() => {
            setShowWebSdk(false);
            setLoading(false);
          }}>
            <XIcon />
          </div>
        </div>
        <div className="custom-scrollbar" style={{ width: "100%", maxHeight: "600px" }}>
          {loading ? (
            <Loading />
          ) : (
            <SumsubWebSdk
              accessToken={accessToken}
              expirationHandler={accessTokenExpirationHandler}
              config={config}
              options={options}
              onMessage={messageHandler}
              onError={errorHandler}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="general-modal">
      <div className="general-modal__header">
        {t('management_mandate.investor_profile')}
        <div className="general-modal__close" onClick={onClose}>
          <XIcon />
        </div>
      </div>
      <div className="general-modal__content">
        <div style={{ display: 'flex', justifyContent: 'center', fontWeight: 'bold', marginBottom: 12, fontSize: 18 }}>{t('management_mandate.your_risk_profile_is')}: {t(`management_mandate.risk_level_${get(userManagementProfile, 'profileTypeText', '').split('_')[0]}`).toUpperCase()}</div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, fontSize: 14, opacity: 0.7 }}>{t('management_mandate.your_management_profile_is')}: {t(`management_mandate.${get(userManagementProfile, 'profileTypeText', '').split('_')[0]}`).toUpperCase()}</div>
        {t('management_mandate.investor_profile_content')}
        <div style={{ overflow: "hidden" }}>
          {userManagementProfile.defiMandate && <DefiTable />}
          {userManagementProfile.cefiMandate && <CefiTable />}
        </div>
      </div>
      <div className="general-modal__footer">
        <button className={'cta-button cta-medium cta-red'} onClick={reset}>
          {t('management_mandate.restart_questionnaire')}
        </button>
        <button onClick={() => setModal({ managementProfileType: true })} className={'cta-button cta-medium cta-blue'}>
          {t('management_mandate.change_risk_profile')}
        </button>
        {
          (userManagementProfile.cefiMandate || userManagementProfile.defiMandate) ? (
            <Button
              onClick={handleConfirm}
              type={EButtonTypes.BLUE}
              className={`cta-medium`}
            >
              <span>{t('common.sign')}</span>
            </Button>
          ) : (
            <button onClick={handleConfirm} className={'cta-button cta-medium cta-blue'}>
              {t('common.confirm')}
            </button>
          )
        }
      </div>
    </div>
  )
}

export default InvestorProfile;

const DefiTable = () => {
  const { t } = useTranslation()
  return (
    <div className="management-mandate-table-container">
      <table>
        <thead>
          <tr>
            <th>MAX. <br /> {t('management_mandate.allocations')}</th>
            <th>{t('management_mandate.serenity').toUpperCase()}</th>
            <th>{t('management_mandate.growth').toUpperCase()}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>Stablecoins</th>
            <td>
              100%
            </td>
            <td>
              Min.: 30%
              <br />
              Max.: 70%
            </td>
          </tr>
          <tr>
            <th>Altcoins</th>
            <td>
              0%
            </td>
            <td>
              Min.: 30%
              <br />
              Max.: 70%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const CefiTable = () => {
  const { t } = useTranslation()
  return (
    <div className="management-mandate-table-container">
      <table>
        <thead>
        <tr>
          <th>MAX. <br /> {t('management_mandate.allocations')}</th>
          <th>{t('management_mandate.defensive').toUpperCase()}</th>
          <th>{t('management_mandate.intermediary').toUpperCase()}</th>
          <th>{t('management_mandate.dynamic').toUpperCase()}</th>
          <th>{t('management_mandate.performance').toUpperCase()}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th>{t('management_mandate.cash')}<br />(Stablecoins)</th>
          <td>
            Min: 0%
            <br />
            Max: 100%
          </td>
          <td>
            Min: 15%
            <br />
            Max: 100%
          </td>
          <td>
            Min: 10%
            <br />
            Max: 100%
          </td>
          <td>
            Min: 0%
            <br />
            Max: 100%
          </td>
        </tr>
        <tr>
          <th>{t('management_mandate.large_cap')}.<br /><span>{t('management_mandate.market_cap')}.: $60Bns - $1Tn</span></th>
          <td>
            Min: 0%
            <br />
            Max: 100%
          </td>
          <td>
            Min: 0%
            <br />
            Max: 70%
          </td>
          <td>
            Min: 0%
            <br />
            Max: 95%
          </td>
          <td>
            Min: 50%
            <br />
            Max: 100%
          </td>
        </tr>
        <tr>
          <th>Altcoins<br />({t('management_mandate.including')} DVX)<br /><span>{t('management_mandate.market_cap')}.: $3M - $10Bns</span></th>
          <td>
            Min: 0%
            <br />
            Max: 0%
          </td>
          <td>
            Min: 5%
            <br />
            Max: 20%
          </td>
          <td>
            Min: 5%
            <br />
            Max: 25%
          </td>
          <td>
            Min: 5%
            <br />
            Max: 30%
          </td>
        </tr>
        <tr>
          <th>Memecoins<br /><span>{t('management_mandate.market_cap')}.: $500M - $3Bns</span></th>
          <td>
            Min: 0%
            <br />
            Max: 0%
          </td>
          <td>
            Min: 0%
            <br />
            Max: 20%
          </td>
          <td>
            Min: 0%
            <br />
            Max: 25%
          </td>
          <td>
            Min: 0%
            <br />
            Max: 30%
          </td>
        </tr>
      </tbody>
      </table>
    </div>
  )
}
